import React, { useEffect, useState, useMemo, useCallback } from 'react';

import { Box, CircularProgress, DialogContent, MenuItem, Select, TextField } from '@mui/material';
import { get } from 'lodash';
import { reaction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { compose } from 'utils/compose';

import { Hr } from 'components/DeliveryManager';
import DialogHeader from './DialogHeader';
import { DialogActionButton, DialogFooter } from 'components/Dialog';
import { classes, StyledDialog } from './styles';

import useSearchParams from 'hooks/useSearchParams';
import api from 'utils/api';
import {
  ADDRESS_MISSING_UNIT,
  DELIVERY_ADDRESS,
  INCIDENT_ADDRESS_MISSING_UNIT,
  invalidAddressIncidents,
  invalidAddressReasons,
} from 'constants/common';
import { colors } from 'themes/colors';

const MAX_TIMEOUT_WS = 1 * 60 * 1000;
const SECONDS = 1000;
const MAX_UNITS_TO_SHOW = 20;

const formatAddress = (addr) => {
  if (!addr) return '';
  return [addr.street, addr.street2, `${addr.city} ${addr.state} ${addr.zipcode}`].filter(Boolean).join(', ');
};

const getEffectiveStreet2 = (selectedUnit, text) => {
  return selectedUnit ? `# ${selectedUnit}` : text;
};

const ContinueDialog = ({ open, onClose, onConfirm, msgError, street, selectedUnit, text }) => {
  const line2 = getEffectiveStreet2(selectedUnit, text);
  const invalidValue = msgError.includes('Street1') ? street : line2;
  const addressLine = msgError.includes('Street1') ? 'Address Line 1' : 'Address Line 2';

  return (
    <StyledDialog open={open} maxWidth="md" fullWidth className={classes.root}>
      <DialogHeader disableTypography title="Confirmation" />
      <DialogContent>
        <p className={classes.description}>
          Your entered value of {addressLine} <span className={classes.warning}>"{invalidValue}"</span> appears to be{' '}
          <span className={classes.warning}>invalid</span>.
        </p>
        <p>Do you still want to save it?</p>
      </DialogContent>
      <DialogFooter>
        <DialogActionButton size="medium" variant="outlined" color="primary" onClick={onClose} disableElevation>
          Cancel
        </DialogActionButton>
        <DialogActionButton size="medium" variant="contained" color="primary" onClick={onConfirm} disableElevation>
          Save
        </DialogActionButton>
      </DialogFooter>
    </StyledDialog>
  );
};

const ContactSupportDialog = ({ open, onClose, onCancel, msgError }) => (
  <StyledDialog open={open} maxWidth="md" fullWidth className={classes.root}>
    <DialogHeader disableTypography title="Contact support" />
    <DialogContent>{msgError}</DialogContent>
    <DialogFooter>
      <a className={`${classes.hyperlink} ${classes.hyperlinkButton}`} href="mailto:support@gojitsu.com">
        <DialogActionButton size="medium" variant="contained" color="primary" onClick={onCancel} disableElevation>
          Contact Support
        </DialogActionButton>
      </a>
      <DialogActionButton size="medium" variant="outlined" color="primary" onClick={onClose} disableElevation>
        Cancel
      </DialogActionButton>
    </DialogFooter>
  </StyledDialog>
);

const MessageAboveFold = ({ isMissingUnit, enableEditAddress1, editFieldAddress }) => {
  if (isMissingUnit) {
    return (
      <p className={classes.warning}>
        The address on file indicates we may be missing or have an incorrect unit number. Please update it in the Address Line 2 below.
      </p>
    );
  }

  if (enableEditAddress1 || editFieldAddress) {
    return null;
  }

  return (
    <p className={classes.text}>
      While Jitsu will attempt to complete your delivery change requests, you will not be able to update the scheduled delivery date.
    </p>
  );
};

const Address1NotAvailable = ({ editField, canEditAddress1 }) => {
  if ((editField === 'street' || editField === 'address') && !canEditAddress1) {
    return (
      <Box color={'red'} fontSize={14}>
        Address line 1 is not available to update right now, please check back later.
      </Box>
    );
  }
  return null;
};

const MessageBelowFold = ({ isMissingUnit, isAddressInvalid, enableEditAddress1, editFieldAddress, editField, canEditAddress1 }) => {
  const supportLink = (
    <a className={classes.hyperlink} href="mailto:support@gojitsu.com">
      support
    </a>
  );

  if (isMissingUnit) {
    return <p className={classes.description}>To change the full address, please contact {supportLink}.</p>;
  }

  if (isAddressInvalid) {
    const addressLinesMessage = enableEditAddress1 ? 'the Address Lines' : 'Address Line 2';
    return (
      <>
        <p className={classes.description}>
          We can only assist you to change {addressLinesMessage} online. To change the full address, please contact {supportLink}.
        </p>
        <Address1NotAvailable editField={editField} canEditAddress1={canEditAddress1} />
      </>
    );
  }

  const editMessage =
    editFieldAddress && enableEditAddress1
      ? 'We can only assist you to change Address Lines 1 and 2 online.'
      : `We can only assist you to change Address Line ${enableEditAddress1 ? '' : '2'} online.`;

  return (
    <>
      <p className={classes.description}>
        {editMessage} To change the full address, please contact {supportLink}.
      </p>
      <Address1NotAvailable editField={editField} canEditAddress1={canEditAddress1} />
    </>
  );
};

const AddressLine2 = ({ text, isMissingUnit, suggestedUnits, selectedUnit, setSelectedUnit, setText }) => {
  if (isMissingUnit && suggestedUnits?.length > 0 && suggestedUnits.length <= MAX_UNITS_TO_SHOW) {
    return (
      <Select placeholder="Unit Number" variant="outlined" onChange={(e) => setSelectedUnit(e.target.value)} value={selectedUnit || ''}>
        {suggestedUnits.map((unitNumber) => (
          <MenuItem key={`unit_${unitNumber}`} value={unitNumber}>
            {unitNumber}
          </MenuItem>
        ))}
      </Select>
    );
  }

  return (
    <TextField
      size="small"
      variant="outlined"
      fullWidth
      value={text}
      onChange={(e) => setText(e.target.value)}
      slotProps={{
        input: { className: classes.input },
        htmlInput: { 'data-testid': 'input-address-2' },
      }}
    />
  );
};

function AddressDialog({ store }) {
  const { trackingCode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const { deliveryStore } = store;
  const { delivery, loadingDelivery, editField } = deliveryStore;

  const address = delivery?.shipment?.dropoff_address;
  const address2 = address?.street2 || '';
  const shipmentID = delivery?.shipment?.id;
  const reason = searchParams.get('reason');
  const incidentType = delivery?.incident?.type;
  const suggestedUnits = delivery?.suggested_units;
  const allowEditStreet1 = get(delivery, 'allow_editing_delivery_address', false);

  const [loading, setLoading] = useState(false);
  const [text, setText] = useState(address2);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [continueDialogOpen, setContinueDialogOpen] = useState(false);
  const [contactSupportDialogOpen, setContactSupportDialogOpen] = useState(false);
  const [street, setStreet] = useState(get(address, 'street', ''));
  const [msgError, setMsgError] = useState('');
  const [enableEditAddress1, setEnableEditAddress1] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [intervalID, setIntervalID] = React.useState(null);
  const [counterTimeWS, setCounterTimeWS] = React.useState(0);

  const editFieldAddress = useMemo(() => editField === 'address', [editField]);

  const isAddressInvalid = useMemo(
    () => delivery?.incident !== undefined && (invalidAddressReasons.includes(reason) || invalidAddressIncidents.includes(incidentType)),
    [delivery?.incident, reason, incidentType],
  );

  const canEditAddress1 = useMemo(
    () => allowEditStreet1 && enableEditAddress1 && (editFieldAddress || isAddressInvalid),
    [allowEditStreet1, enableEditAddress1, editFieldAddress, isAddressInvalid],
  );

  const isMissingUnit = useMemo(() => reason === ADDRESS_MISSING_UNIT || incidentType === INCIDENT_ADDRESS_MISSING_UNIT, [reason, incidentType]);

  const correctedInfo = useMemo(
    () => delivery?.customer_profile?.corrected_address || address,
    [delivery?.customer_profile?.corrected_address, address],
  );

  const originAddress = useMemo(() => formatAddress(address), [address]);
  const correctedAddress = useMemo(() => formatAddress(correctedInfo), [correctedInfo]);

  const differenceAddress = useMemo(() => originAddress?.toLowerCase() !== correctedAddress?.toLowerCase(), [originAddress, correctedAddress]);

  const increment = useCallback(() => setCounterTimeWS((oldCounter) => oldCounter + SECONDS), []);

  const handleCancel = useCallback(() => {
    searchParams.set('dialog', 'closed');
    deliveryStore.updateField('editField', '');
    setLoading(false);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams, deliveryStore]);

  useEffect(() => {
    const dispose = reaction(
      () => deliveryStore.waitingWS,
      () => {
        if (deliveryStore.waitingWS === false && loading === true) {
          setLoading(false);
          setIntervalID(null);
          setCounterTimeWS(0);
          handleCancel();
        }
      },
    );

    return () => dispose();
  }, [deliveryStore, loading, handleCancel]);

  useEffect(() => {
    const clientID = get(delivery, 'client.id');
    const regionCode = get(delivery, 'shipment.region_code');

    if (clientID) {
      setIsFetching(true);
      deliveryStore.getAppFeatures([`CL_${clientID}`, `RG_${regionCode}`, 'AP_DEFAULT']).then((res) => {
        setIsFetching(false);
        if (!res.ok) {
          return;
        }

        const { allow_edit_address_1 } = res.data;
        setEnableEditAddress1(allow_edit_address_1 || false);
      });
    }
  }, [delivery, deliveryStore]);

  useEffect(() => {
    if (counterTimeWS >= MAX_TIMEOUT_WS) {
      toast.error('Address updated fail', { hideProgressBar: true, theme: 'colored' });
      setContinueDialogOpen(false);
      clearInterval(intervalID);
      setIntervalID(null);
      setLoading(false);
      setCounterTimeWS(0);
    }
  }, [counterTimeWS]);

  const doSubmit = useCallback(() => {
    if (intervalID === null) {
      setIntervalID(setInterval(increment, SECONDS));
    }

    deliveryStore.updateField('waitingWS', true);
    setLoading(true);

    const data = {
      street,
      street2: getEffectiveStreet2(selectedUnit, text),
    };

    api
      .post(`delivery/${trackingCode}/${shipmentID}/edit`, data, {
        params: { keep_profile_info: true },
      })
      .then((response) => {
        if (response.status !== 200) {
          deliveryStore.updateField('waitingWS', false);
          setLoading(false);
          return toast.error(response?.data?.message || 'Something went wrong. Please try again!', { hideProgressBar: true, theme: 'colored' });
        }

        if (!canEditAddress1 || street === get(address, 'street', '')) {
          toast.success('Address updated successfully!', { hideProgressBar: true, theme: 'colored' });
          setLoading(false);
          deliveryStore.getDelivery(trackingCode);
          handleCancel();
        }
      });
  }, [intervalID, increment, deliveryStore, street, selectedUnit, text, trackingCode, shipmentID, editFieldAddress, handleCancel]);

  const handleSubmit = useCallback(() => {
    const effectiveStreet2 = getEffectiveStreet2(selectedUnit, text);
    const data = { street, street2: effectiveStreet2 };

    if (isAddressInvalid && canEditAddress1 && !street) {
      return toast.error('Please enter address 1', { hideProgressBar: true, theme: 'colored' });
    }

    if ((editFieldAddress) && canEditAddress1 && !street) {
      return toast.error('Please enter address 1', { hideProgressBar: true, theme: 'colored' });
    }

    if ((editFieldAddress) && street === get(address, 'street', '') && effectiveStreet2 === address2) {
      return toast.warn('Data is not changed', { hideProgressBar: true, theme: 'colored' });
    }

    deliveryStore.updateField('waitingWS', true);

    if (selectedUnit) {
      doSubmit();
      return;
    }

    setMsgError('');
    api.post(`delivery/${trackingCode}/${shipmentID}/validate-edit`, data).then((response) => {
      if (response.status !== 200) {
        setLoading(false);
        if (response.status === 400) {
          const errorMessage = get(response, 'data.message');
          if (errorMessage.includes('Street1')) {
            setMsgError('We are unable to validate your address. Please Contact Support.');
            setContactSupportDialogOpen(true);
          } else {
            setMsgError(errorMessage);
            setContinueDialogOpen(true);
          }
        } else {
          toast.error(response?.data?.message || 'Something went wrong. Please try again!', {
            hideProgressBar: true,
            theme: 'colored',
          });
        }
      } else {
        doSubmit();
      }
    });
  }, [
    selectedUnit,
    text,
    street,
    isAddressInvalid,
    canEditAddress1,
    editFieldAddress,
    address,
    address2,
    deliveryStore,
    trackingCode,
    shipmentID,
    doSubmit,
  ]);

  const getDialogTitle = useCallback(() => {
    if (reason === DELIVERY_ADDRESS) return 'Address';
    if (isMissingUnit) return 'Update Unit Number';
    if (isAddressInvalid) return 'Invalid Address!';
    return 'Address';
  }, [reason, isMissingUnit, isAddressInvalid]);

  if (!editField && !isMissingUnit && !isAddressInvalid) return null;

  if (loading || loadingDelivery || isFetching) {
    return (
      <StyledDialog open fullWidth maxWidth="md" className={classes.root}>
        <Box height={300} display={'flex'} flexDirection={'column'} alignItems={'center'} justifyContent={'center'}>
          <CircularProgress color="primary" />
          {deliveryStore.waitingWS && <Box mt={2}>Updating your address, please do not close the dialog</Box>}
        </Box>
      </StyledDialog>
    );
  }

  return (
    <>
      <StyledDialog open={Boolean(editField) || isMissingUnit || isAddressInvalid} maxWidth="md" fullWidth className={classes.root}>
        <DialogHeader title={getDialogTitle()} />
        <DialogContent>
          {differenceAddress && (
            <Box fontWeight={600} mb={1}>
              <span className={classes.label}>Corrected Address: </span>
              <span style={{ color: colors.green }}>{correctedAddress}</span>
            </Box>
          )}
          <MessageAboveFold
            isMissingUnit={isMissingUnit}
            enableEditAddress1={enableEditAddress1}
            editFieldAddress={editFieldAddress}
          />
          <Hr />
          <MessageBelowFold
            isMissingUnit={isMissingUnit}
            isAddressInvalid={isAddressInvalid}
            enableEditAddress1={enableEditAddress1}
            editFieldAddress={editFieldAddress}
            editField={editField}
            canEditAddress1={canEditAddress1}
          />

          <div className={classes.form}>
            <p className={classes.label}>Address 1:</p>
            {canEditAddress1 ? (
              <Box display={'flex'} alignItems={'center'} style={{ gap: 16 }}>
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  slotProps={{
                    input: { className: classes.input },
                    htmlInput: { 'data-testid': 'input-address-1' },
                  }}
                />
              </Box>
            ) : (
              <p className={classes.value} data-testid="address-1-value">
                {address?.street || 'N/A'}
              </p>
            )}
          </div>

          <div className={classes.form}>
            <p className={classes.label}>Address 2:</p>
            <AddressLine2
              text={text}
              isMissingUnit={isMissingUnit}
              suggestedUnits={suggestedUnits}
              selectedUnit={selectedUnit}
              setSelectedUnit={setSelectedUnit}
              setText={setText}
            />
          </div>

          <div className={classes.form}>
            <p className={classes.label}>City:</p>
            <p className={classes.value} data-testid="city-value">
              {address?.city}
            </p>
          </div>

          <div className={classes.form}>
            <p className={classes.label}>State:</p>
            <p className={classes.value} data-testid="state-value">
              {address?.state}
            </p>
          </div>

          <div className={classes.form}>
            <p className={classes.label}>Zipcode:</p>
            <p className={classes.value} data-testid="zipcode-value">
              {address?.zipcode}
            </p>
          </div>
        </DialogContent>

        <DialogFooter>
          <DialogActionButton size="medium" variant="outlined" color="primary" onClick={handleCancel} disableElevation>
            Cancel
          </DialogActionButton>
          <DialogActionButton
            size="medium"
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disableElevation
          >
            Save
          </DialogActionButton>
        </DialogFooter>
      </StyledDialog>

      <ContinueDialog
        open={continueDialogOpen}
        onClose={() => setContinueDialogOpen(false)}
        onConfirm={doSubmit}
        msgError={msgError}
        street={street}
        selectedUnit={selectedUnit}
        text={text}
      />

      <ContactSupportDialog
        open={contactSupportDialogOpen}
        onClose={() => setContactSupportDialogOpen(false)}
        onCancel={handleCancel}
        msgError={msgError}
      />
    </>
  );
}

export default compose(inject('store'), observer)(AddressDialog);
